<template>
  <div class="shop-categories">

    <!-- Lista kategorija -->
    <div class="cats-list">

      <!-- displayMode: 'icon' — lista s ikonom i akcentom u boji -->
      <template v-if="displayMode === 'icon'">
        <div
          v-for="(cat, idx) in categories"
          :key="cat.id"
          class="cat-entry"
          :class="{ sel: cat.id === selectedCategoryId }"
          :style="entryStyle(idx, cat.id === selectedCategoryId)"
          :title="cat.name"
          @click="$emit('select-category', cat.id)"
        >
          <img v-if="cat.picture" :src="cat.picture" class="cat-icon" loading="lazy" />
          <v-icon v-else small :color="colorFromId(cat.id).accent" class="cat-icon-placeholder">
            mdi-circle-slice-8
          </v-icon>
          <span class="cat-name" :style="{ fontSize: fontSize + 'px' }">{{ cat.name }}</span>
        </div>
      </template>

      <!-- displayMode: 'card' — kartice s fiksnom visinom i bojama -->
      <template v-else>
        <div
          v-for="(cat, idx) in categories"
          :key="cat.id"
          class="cat-card"
          :class="{ sel: cat.id === selectedCategoryId }"
          :style="cardStyle(idx, cat.id === selectedCategoryId)"
          :title="cat.name"
          @click="$emit('select-category', cat.id)"
        >
          <img v-if="cat.picture" :src="cat.picture" class="cat-card-img" loading="lazy" />
          <span class="cat-card-name" :style="{ fontSize: fontSize + 'px' }">{{ cat.name }}</span>
        </div>
      </template>

    </div>

    <!-- Toggle gumbi na dnu — rijetko se koriste -->
    <div class="cats-foot">
      <button
        v-for="m in displayModes"
        :key="m.value"
        class="cats-mode-btn"
        :class="{ active: displayMode === m.value }"
        :title="m.title"
        @click="$emit('change-display-mode', m.value)"
      >
        <v-icon size="13">{{ m.icon }}</v-icon>
      </button>
      <div class="cats-font-ctrl">
        <v-tooltip top max-width="180" color="white" content-class="vpc-tooltip">
          <template v-slot:activator="{ on }">
            <button class="cats-mode-btn" :disabled="fontSize <= 10" v-on="on" @click="changeFontSize(-1)">
              <v-icon size="13">mdi-format-font-size-decrease</v-icon>
            </button>
          </template>
          <span style="font-size:12px;color:#1e293b;padding:6px 10px;display:block;">Smanji veličinu teksta kategorija</span>
        </v-tooltip>
        <v-tooltip top max-width="180" color="white" content-class="vpc-tooltip">
          <template v-slot:activator="{ on }">
            <button class="cats-mode-btn" :disabled="fontSize >= 20" v-on="on" @click="changeFontSize(1)">
              <v-icon size="13">mdi-format-font-size-increase</v-icon>
            </button>
          </template>
          <span style="font-size:12px;color:#1e293b;padding:6px 10px;display:block;">Povećaj veličinu teksta kategorija</span>
        </v-tooltip>
      </div>
    </div>

  </div>
</template>

<script>

export default {
  name: 'ShopCategories',

  props: {
    categories: { type: Array, default: () => [] },
    selectedCategoryId: { type: String, default: '' },
    displayMode: { type: String, default: 'icon' },
    fontSize: { type: Number, default: 14 }
  },

  data () {
    return {
      displayModes: [
        { value: 'icon', icon: 'mdi-format-list-bulleted', title: 'Lista' },
        { value: 'card', icon: 'mdi-view-grid-outline', title: 'Kartice' }
      ]
    }
  },

  methods: {
    colorFromId (id) {
      let h = 0
      for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
      // Golden angle distribucija — maksimalna razlika između susjednih boja
      const hue = (h * 137.508) % 360
      return {
        bg: `hsl(${hue}, 55%, 95%)`,
        selected: `hsl(${hue}, 55%, 88%)`,
        accent: `hsl(${hue}, 45%, 50%)`
      }
    },
    entryStyle (idx, isSelected) {
      const c = this.colorFromId(this.categories[idx]?.id || String(idx))
      return isSelected
        ? { backgroundColor: c.selected }
        : { borderLeftColor: c.accent, backgroundColor: c.bg }
    },
    changeFontSize (delta) {
      this.$emit('change-font-size', delta)
    },
    cardStyle (idx, isSelected) {
      const c = this.colorFromId(this.categories[idx]?.id || String(idx))
      return isSelected
        ? { backgroundColor: c.selected, borderColor: 'rgba(0,188,212,0.25)' }
        : { borderLeftColor: c.accent, backgroundColor: c.bg, borderColor: 'rgba(0,0,0,0.07)' }
    }
  }
}
</script>

<style scoped>
.shop-categories {
  width: 240px; /* override via inline style iz ShopRegister */
  background: #F8FAFC;
  border-right: 1px solid rgba(0,0,0,0.06);
  flex-shrink: 0;
  height: 100%;
  position: relative;
}

/* Lista kategorija */
.cats-list {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 46px;
  overflow-y: auto;
  padding: 6px 0;
  scrollbar-width: thin;
  scrollbar-color: rgba(0,188,212,0.5) transparent;
}
.cats-list::-webkit-scrollbar { width: 5px; }
.cats-list::-webkit-scrollbar-track { background: transparent; }
.cats-list::-webkit-scrollbar-thumb { background: rgba(0,188,212,0.5); border-radius: 3px; }
.cats-list::-webkit-scrollbar-thumb:hover { background: rgba(0,188,212,0.8); }

/* ── ICON MODE ── */
.cat-entry {
  margin: 3px 8px;
  padding: 9px 10px;
  cursor: pointer;
  border: 1px solid rgba(0,0,0,0.07);
  border-left-width: 3px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.15s, box-shadow 0.15s;
  user-select: none;
}
.cat-entry:hover { opacity: 0.85; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
.cat-entry.sel {
  border-left-width: 4px !important;
  border-left-color: #00BCD4 !important;
  margin-left: 12px !important;
  box-shadow: 0 3px 12px rgba(0,0,0,0.13);
}
.cat-entry.sel .cat-name { color: #1a202c; font-weight: 800; }
.cat-icon { width: 18px; height: 18px; border-radius: 3px; object-fit: cover; flex-shrink: 0; }
.cat-icon-placeholder { flex-shrink: 0; }
.cat-name {
  flex: 1;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── CARD MODE ── */
.cat-card {
  margin: 4px 8px;
  padding: 10px 10px 10px 12px;
  min-height: 68px;
  border: 1px solid rgba(0,0,0,0.07);
  border-left-width: 4px;
  border-radius: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: opacity 0.15s, box-shadow 0.15s;
  user-select: none;
}
.cat-card:hover { opacity: 0.85; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
.cat-card.sel {
  border-left-width: 4px !important;
  border-left-color: #00BCD4 !important;
  margin-left: 12px !important;
  box-shadow: 0 3px 12px rgba(0,0,0,0.13);
}
.cat-card.sel .cat-card-name { color: #1a202c; font-weight: 800; }
.cat-card-img {
  width: 28px; height: 28px;
  border-radius: 6px; object-fit: cover;
  flex-shrink: 0;
}
.cat-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  text-align: left;
  text-transform: uppercase;
  line-height: 1.45;
  /* max 2 linije */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

/* ── FOOTER: toggle gumbi ── */
.cats-foot {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 46px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  border-top: 1px solid rgba(0,0,0,0.05);
  background: #F8FAFC;
  z-index: 1;
}
.cats-mode-btn {
  width: 28px; height: 24px;
  border: 1px solid #E0E6ED;
  border-radius: 6px;
  background: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: #B0BEC5;
  transition: all 0.15s;
}
.cats-mode-btn:hover:not(:disabled) { border-color: #00BCD4; color: #00BCD4; }
.cats-mode-btn.active { border-color: #00BCD4; background: rgba(0,188,212,0.12); color: #00BCD4; }
.cats-mode-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.cats-font-ctrl { display: flex; gap: 4px; margin-left: 4px; padding-left: 6px; border-left: 1px solid #E0E6ED; }
</style>
